import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import i18n from "../../i18n";
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import { Button } from "../button";
import CheckboxInput from "../inputs/CheckboxInput";
import { useGetValidatePhoto } from "../../hooks/common/query";
import { useUpdateValidatePhoto } from "../../hooks/common/mutation";
import toast from "react-hot-toast";
import Loader from "../loader";

export const ValidatePhotoFRConfigurationsCardSchema = Yup.object({
  ipAddresses: Yup.string().required(
    i18n.language === "en" ? "IP Address is required." : "عنوان IP مطلوب."
  ),
});

interface FormData {
  ipAddresses: string;
}

const ValidatePhotoFRConfigurationsCard = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useFormWithSchema(ValidatePhotoFRConfigurationsCardSchema);

  const [isValidatePhoto, setIsValidatePhoto] = useState<boolean>(false);

  const { data: validatePhoto, isPending: isValidatePhotoLoading } =
    useGetValidatePhoto();

  const {
    mutateAsync: UpdateValidatePhoto,
    isPending: isUpdateValidatePhotoLoading,
  } = useUpdateValidatePhoto();

  useEffect(() => {
    if (validatePhoto) {
      reset({
        ipAddresses: validatePhoto.ipAddresses,
      });
      setIsValidatePhoto(validatePhoto.isValidatePhotoFromFR);
    }
  }, [reset, validatePhoto]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const body = {
          ipAddresses: data.ipAddresses,
          isValidatePhotoFromFR: isValidatePhoto,
        };

        await UpdateValidatePhoto(body);
        toast.success(t("validatePhotoConfigurationUpdatedSuccessfully"));
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [UpdateValidatePhoto, isValidatePhoto, t]
  );

  return (
    <div className="flex flex-col gap-2 lg:gap-3">
      <h2 className="text-lg lg:text-xl font-medium">
        {t("validatePhotoFRConfiguration")}
      </h2>

      {isValidatePhotoLoading ? (
        <Loader />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-2 md:gap-4"
        >
          <div className="sm:w-1/2 lg:w-1/3">
            <Controller
              name="ipAddresses"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("ipAddresses")}
                  onChange={onChange}
                  value={value || ""}
                  error={errors?.[name]?.message}
                  required
                />
              )}
            />
          </div>
          <CheckboxInput
            key="validate-photo"
            id="validate-photo"
            label={t("validatePhoto")}
            checked={isValidatePhoto}
            setChecked={() => setIsValidatePhoto((prev) => !prev)}
          />
          <Button
            isLoading={isUpdateValidatePhotoLoading}
            className="max-w-max !py-[14px] !px-9"
          >
            {t("update")}
          </Button>
        </form>
      )}
    </div>
  );
};

export default React.memo(ValidatePhotoFRConfigurationsCard);
